import { Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { TooltipProps } from '@mui/material/Tooltip';
import { tooltipClasses } from '@mui/material/Tooltip';

import IconButton from '../IconButton';
import Popover from '../Popover';
import ReverseGradientButton from '../ReverseGradientButton';

export const StyledPopover = styled(Popover)({
  '& .MuiPaper-root': {
    marginTop: '0.8rem',

    backgroundColor: 'rgba(0, 0, 0, 0.6)',

    '& > div': {
      width: '38rem',
    },
  },
});

export const Container = styled('div')(({ theme }) => ({
  border: `0.1rem solid ${theme.colors.base05}`,

  borderRadius: '1rem',

  backgroundColor: theme.colors.base07,
}));

export const TopContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',

  padding: '1.8rem 2.4rem',

  borderBottom: `0.1rem solid ${theme.colors.base05}`,
}));

export const TopLeftContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',

  columnGap: '1rem',
});

type BodyContainerProps = {
  'data-is-small-device': boolean;
};

export const BodyContainer = styled('div')<BodyContainerProps>(({ ...props }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',

  rowGap: '2rem',

  padding: '2.4rem',

  maxHeight: props['data-is-small-device'] ? '55rem' : undefined,
  overflow: props['data-is-small-device'] ? 'auto' : undefined,
}));

export const BodyItemContainer = styled('div')(({ theme }) => ({
  width: '100%',

  padding: '1.3rem 1.6rem 1.6rem',

  borderRadius: '1rem',

  backgroundColor: theme.colors.base06,
}));

export const BodyItemTopContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',

  paddingBottom: '1.4rem',
  marginBottom: '1.6rem',

  borderBottom: `0.1rem solid ${theme.colors.base04}`,
}));

export const BodyItemTopLeftContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',

  columnGap: '0.6rem',

  color: theme.colors.base01,
}));

export const BodyItemTopIconContainer = styled('div')({
  width: '2.4rem',
  height: '2.4rem',

  '& > svg': {
    width: '2.4rem',
    height: '2.4rem',
  },
});

export const BodyItemTopRightContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',

  columnGap: '0.4rem',
});

export const BodyItemLeftContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',

  columnGap: '1.2rem',
});

export const TokenContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',

  rowGap: '1.2rem',
});

export const Base02TextContainer = styled('div')(({ theme }) => ({
  color: theme.colors.base02,
}));

export const BodyTokenContainer = styled('div')({
  marginBottom: '0.4rem',
});

export const BodyTokenBalanceContainer = styled('div')(({ theme }) => ({
  width: '100%',

  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',

  columnGap: '1rem',

  color: theme.colors.base01,
}));

export const ImageContainer = styled('div')({
  width: '2.0rem',
  height: '2.0rem',

  '& > svg': {
    width: '2.0rem',
    height: '2.0rem',
  },
});

export const BalanceContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

export const BodyAddressContainer = styled('div')({
  width: '100%',

  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
});

export const StyledReverseGradientButton = styled(ReverseGradientButton)(({ theme }) => ({
  width: '100%',
  height: '5rem',

  border: `0.1rem solid ${theme.colors.base04}`,
}));

export const IconContainer = styled('div')({
  width: '2.4rem',
  height: '2.4rem',

  '& > svg': {
    width: '2.4rem',
    height: '2.4rem',
  },
});

export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  '&:hover': {
    '& > svg': {
      '& > path': {
        fill: theme.colors.base03,
      },
    },
  },
}));

export const WalletDisconnectButton = styled(IconButton)(({ theme }) => ({
  marginLeft: '-0.2rem',
  '&:hover': {
    '& > svg': {
      '& > path': {
        fill: theme.colors.base03,
      },
    },
  },
}));

export const StyledTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} arrow classes={{ popper: className }} />)({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#2E2E2E',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#2E2E2E',
    padding: '0.4rem 1.5rem',
    borderRadius: '0.6rem',
    fontSize: '1.4rem',
  },
});

export const BottomContainer = styled('div')({
  width: '100%',

  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',

  marginTop: '2.4rem',

  rowGap: '0.8rem',
});

export const URLButton = styled('button')({
  padding: 0,
  border: 0,

  color: '#69A5FF',

  backgroundColor: 'transparent',

  cursor: 'pointer',

  '&:hover': {
    opacity: 0.8,
  },

  textAlign: 'left',
});

export const Divider = styled('div')(({ theme }) => ({
  width: '100%',
  height: '0.1rem',

  margin: '1.6rem 0',

  backgroundColor: theme.colors.base04,
}));
